import {
  UsersThree,
  CalendarCheck,
  BellRinging,
  Door,
  Receipt,
  FileText,
  Sparkle,
  Headset,
  WhatsappLogo,
  Check,
  ArrowLeft,
} from "@phosphor-icons/react";
import lennyImage from "../../../assets/lenny_right_side_hey.png";
import MarkerStroke from "./MarkerStroke";
import useReveal from "./useReveal";
import { trackPixelEvent } from "../../../hooks/useFacebookPixel";
import "./home.css";
import "./HomeLenny.css";

/*
 * Lenny + human support — answers the two objections that stop a therapist
 * from switching: "it's probably complicated" and "who helps me when I'm
 * stuck at 22:00". Left: capability list ("deep, yet simple"). Right: chat
 * mock with Lenny, then two support cards (AI, always / humans, always).
 */

const CAPABILITIES = [
  { icon: UsersThree, text: "קליניקות עם כמה מטפלים: הרשאות, שיתוף מטופלים ויומנים נפרדים" },
  { icon: CalendarCheck, text: "קביעת תורים עצמאית למטופלים לפי הזמינות שאתם מגדירים" },
  { icon: BellRinging, text: "יומן חכם עם תזכורות SMS, אישורי הגעה והתראות לפי בחירתכם" },
  { icon: Door, text: "ניהול חדרי טיפול ושיבוץ אוטומטי בלי התנגשויות" },
  { icon: Receipt, text: "גבייה, חשבוניות ודוחות הכנסות במקום אחד" },
  { icon: FileText, text: "טפסים לחתימה, שאלונים ותיק מטופל דיגיטלי" },
];

const CHAT = [
  { from: "user", text: "איך אני נותנת למטופלים לקבוע תור לבד רק בימי שלישי אחר הצהריים?" },
  {
    from: "lenny",
    text: "קל. בהגדרות היומן, תחת ״זמינות לקביעה עצמאית״, מסמנים יום שלישי וטווח שעות. מרגע השמירה הלינק האישי שלך מציג רק את השעות האלה. רוצה שאפתח לך את המסך?",
  },
];

function HomeLenny() {
  const ref = useReveal();

  return (
    <section id="support" className="home-section home-section--white hlen" aria-labelledby="hlen-title">
      <div className="home-container">
        <div className="hlen-grid home-reveal" ref={ref}>
          <div className="hlen-copy">
            <h2 id="hlen-title" className="home-h2 home-h2--display">
              מערכת שיודעת לעשות הרבה,{" "}
              <br />
              ונשארת{" "}
              <span className="home-mark">
                פשוטה.
                <MarkerStroke />
              </span>
            </h2>
            <p className="home-sub">
              CliniKit מנהלת קליניקות של מטפל אחד וקליניקות של עשרה, עם כל מה שמסביב. ובכל זאת,
              רוב המטפלים עובדים בה באופן מלא כבר ביום הראשון. ככה זה כשכל מסך נבנה עם מטפלים,
              לא רק עבורם.
            </p>

            <ul className="hlen-caps">
              {CAPABILITIES.map((c) => {
                const Icon = c.icon;
                return (
                  <li key={c.text}>
                    <span className="hlen-cap-icon" aria-hidden="true">
                      <Icon size={18} weight="duotone" />
                    </span>
                    <span>{c.text}</span>
                  </li>
                );
              })}
            </ul>

            <p className="hlen-more">
              <Check size={14} weight="bold" />
              ועוד הרבה. בלי הדרכות של שעות, בלי מדריכים של 40 עמודים.
            </p>
          </div>

          <div className="hlen-side">
            {/* Chat mock */}
            <div className="hlen-chat home-card" aria-hidden="true">
              <div className="hlen-chat-head">
                <img src={lennyImage} alt="" className="hlen-chat-avatar" />
                <div>
                  <strong>לני</strong>
                  <span>העוזרת החכמה של CliniKit · זמינה תמיד</span>
                </div>
                <span className="hlen-online" />
              </div>
              <div className="hlen-chat-body">
                {CHAT.map((m, i) => (
                  <div key={i} className={`hlen-bubble hlen-bubble--${m.from}`}>
                    {m.text}
                  </div>
                ))}
              </div>
            </div>

            {/* Support promises */}
            <div className="hlen-support">
              <div className="hlen-support-card">
                <span className="hlen-support-icon">
                  <Sparkle size={20} weight="fill" />
                </span>
                <h3>לני, העוזרת החכמה</h3>
                <p>
                  מכירה כל מסך במערכת, יודעת איפה אתם נמצאים עכשיו ועונה בעברית תוך שניות.
                  בלילה, בשבת, באמצע פגישה.
                </p>
              </div>
              <div className="hlen-support-card">
                <span className="hlen-support-icon hlen-support-icon--human">
                  <Headset size={20} weight="fill" />
                </span>
                <h3>ובני אדם. תמיד.</h3>
                <p>
                  צוות תמיכה ישראלי בוואטסאפ ובטלפון, והדרכה אישית בכניסה למערכת.
                  לא בוט, לא טיקט. אנשים.
                </p>
                <a
                  href="https://wa.me/972543073737?text=%D7%94%D7%99%D7%99%2C%20%D7%90%D7%A9%D7%9E%D7%97%20%D7%9C%D7%A9%D7%9E%D7%95%D7%A2%20%D7%A2%D7%95%D7%93%20%D7%A2%D7%9C%20CliniKit"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="home-link hlen-wa"
                  onClick={() => trackPixelEvent("Contact", { content_name: "homepage_lenny_whatsapp" })}
                >
                  <WhatsappLogo size={16} weight="fill" />
                  דברו איתנו עכשיו
                  <ArrowLeft size={14} weight="bold" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HomeLenny;
